import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, ChevronDown, ChevronRight, Video, FileText, File,
  ImageIcon, Clock, User, Globe, BookOpen, Loader, Download,
  PlayCircle, Tag, Radio, StopCircle,
} from "lucide-react";
import {
  lmsGetCourseById,
  lmsGetClassSubjects,
  lmsGetChapters,
  lmsGetTopics,
  lmsGetMaterials,
  lmsGetLiveClasses,
} from "../api/lmsApi";
import { useDispatch, useSelector } from "react-redux";
import { fetchLmsCourses } from "../redux/lmsSlice";
import { JoinModal } from "./LiveClasses";
import "../lms.css";

const getSchoolId = () => {
  try {
    const u = JSON.parse(localStorage.getItem("userData") || "{}");
    return u.school_id || u.admin_id || null;
  } catch { return null; }
};

function getYouTubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/))([^&?/\s]{11})/);
  return m ? m[1] : null;
}
function getVimeoId(url) {
  if (!url) return null;
  const m = url.match(/vimeo\.com\/(\d+)/);
  return m ? m[1] : null;
}
function isImage(url) { return /\.(jpg|jpeg|png|gif|webp|svg)(\?|$)/i.test(url || ""); }
function isPdf(url, docType) { return docType === "pdf" || /\.pdf(\?|$)/i.test(url || ""); }

/* ── Content Viewer ── */
function ContentViewer({ material }) {
  if (!material) {
    return (
      <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", background: "#0f0f0f", color: "#6b7280" }}>
        <PlayCircle size={56} color="#374151" />
        <p style={{ marginTop: 16, fontSize: 15 }}>Select a lesson to start learning</p>
      </div>
    );
  }
  const { file_url, material_type, document_type, title, file_name } = material;
  const ytId = getYouTubeId(file_url);
  if (ytId) return (
    <iframe src={`https://www.youtube.com/embed/${ytId}?autoplay=1`} title={title}
      style={{ width: "100%", height: "100%", border: "none" }}
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen />
  );
  const vimeoId = getVimeoId(file_url);
  if (vimeoId) return (
    <iframe src={`https://player.vimeo.com/video/${vimeoId}?autoplay=1`} title={title}
      style={{ width: "100%", height: "100%", border: "none" }} allowFullScreen />
  );
  if (material_type === "video" || /\.(mp4|webm|ogg)(\?|$)/i.test(file_url || "")) return (
    <video controls autoPlay style={{ width: "100%", height: "100%", background: "#000" }} src={file_url}>
      Your browser does not support video.
    </video>
  );
  if (isImage(file_url)) return (
    <img src={file_url} alt={title} style={{ width: "100%", height: "100%", objectFit: "contain", background: "#111" }} />
  );
  if (isPdf(file_url, document_type)) return (
    <iframe src={file_url} title={title} style={{ width: "100%", height: "100%", border: "none" }} />
  );
  return (
    <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center", background: "#f8fafc", gap: 16 }}>
      <div style={{ width: 80, height: 80, borderRadius: 16, background: "#eff6ff",
        display: "flex", alignItems: "center", justifyContent: "center" }}>
        <FileText size={36} color="#1c498d" />
      </div>
      <div style={{ fontSize: 16, fontWeight: 600, color: "#111827" }}>{title || file_name || "File"}</div>
      <a href={file_url} target="_blank" rel="noreferrer" style={{
        display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 24px",
        borderRadius: 8, background: "#1c498d", color: "#fff", fontWeight: 600,
        fontSize: 14, textDecoration: "none" }}>
        <Download size={16} /> Open / Download
      </a>
    </div>
  );
}

function MatTypeIcon({ mat }) {
  const url = mat.file_url || "";
  if (getYouTubeId(url) || getVimeoId(url) || mat.material_type === "video" || /\.(mp4|webm)$/i.test(url))
    return <Video size={13} color="#7c3aed" />;
  if (isImage(url)) return <ImageIcon size={13} color="#0ea5e9" />;
  if (isPdf(url, mat.document_type)) return <FileText size={13} color="#dc2626" />;
  return <File size={13} color="#6b7280" />;
}

/* ── Mobile sidebar (chapter/topic list below video) ── */
function MobileSidebar({ chapters, matLoading, matError, totalMaterials, activeMaterial, setActiveMaterial, openChapters, toggleChapter }) {
  return (
    <div style={{ borderTop: "2px solid #e5e7eb", background: "#fff" }}>
      <div style={{ padding: "12px 16px", borderBottom: "1px solid #e5e7eb", background: "#f8fafc" }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>Course Content</div>
        {!matLoading && !matError && (
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
            {chapters.length} chapters · {totalMaterials} lessons
          </div>
        )}
      </div>
      <div>
        {matLoading ? (
          <div style={{ textAlign: "center", padding: 32 }}><Loader size={20} className="ms2-spin" /></div>
        ) : matError ? (
          <div style={{ padding: 20, color: "#6b7280", fontSize: 13, textAlign: "center" }}>{matError}</div>
        ) : chapters.length === 0 ? (
          <div style={{ padding: 20, color: "#9ca3af", fontSize: 13, textAlign: "center" }}>No materials added yet.</div>
        ) : chapters.map((ch, ci) => {
          const chOpen = !!openChapters[ch.id];
          const matCount = ch.topics.reduce((s, t) => s + t.materials.length, 0);
          return (
            <div key={ch.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
              <button onClick={() => toggleChapter(ch.id)} style={{
                width: "100%", display: "flex", alignItems: "center", gap: 8,
                padding: "12px 16px", background: chOpen ? "#f0f4ff" : "#f9fafb",
                border: "none", cursor: "pointer", textAlign: "left",
              }}>
                {chOpen ? <ChevronDown size={14} color="#4f46e5" /> : <ChevronRight size={14} color="#9ca3af" />}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 12, color: "#1e1b4b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    Ch {ch.chapter_number || ci + 1}. {ch.name}
                  </div>
                  <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 1 }}>
                    {ch.topics.length} topics · {matCount} lessons
                  </div>
                </div>
              </button>
              {chOpen && ch.topics.map((tp, ti) => (
                <div key={tp.id}>
                  <div style={{ padding: "6px 16px 4px 36px", fontSize: 11, fontWeight: 700,
                    color: "#7c3aed", background: "#faf5ff", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    {tp.topic_number || ti + 1}. {tp.name}
                  </div>
                  {tp.materials.map((m) => {
                    const isActive = activeMaterial?.id === m.id;
                    return (
                      <button key={m.id} onClick={() => setActiveMaterial(m)} style={{
                        width: "100%", display: "flex", alignItems: "center", gap: 10,
                        padding: "9px 16px 9px 44px", border: "none", textAlign: "left",
                        background: isActive ? "#eef2ff" : "transparent",
                        borderLeft: isActive ? "3px solid #4f46e5" : "3px solid transparent",
                        cursor: "pointer",
                      }}>
                        <MatTypeIcon mat={m} />
                        <span style={{ flex: 1, fontSize: 12, color: isActive ? "#4338ca" : "#374151",
                          fontWeight: isActive ? 600 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {m.title || m.file_name || "Untitled"}
                        </span>
                        {m.duration_minutes && (
                          <span style={{ fontSize: 11, color: "#9ca3af", flexShrink: 0 }}>{m.duration_minutes}m</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function useWindowWidth() {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const fn = () => setWidth(window.innerWidth);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return width;
}

export default function StudentCourseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const schoolId = getSchoolId();
  const winW = useWindowWidth();
  const isMobile = winW < 768;
  const isTablet = winW >= 768 && winW < 1100;

  const { courses } = useSelector((s) => s.lms);
  const course = courses.find((c) => String(c.id) === String(id));

  const [chapters, setChapters]             = useState([]);
  const [classSubjectId, setClassSubjectId] = useState(null);
  const [matLoading, setMatLoading]         = useState(true);
  const [matError, setMatError]             = useState(null);
  const [openChapters, setOpenChapters]     = useState({});
  const [activeMaterial, setActiveMaterial] = useState(null);
  const [sidebarOpen, setSidebarOpen]       = useState(true);
  const [courseLiveClasses, setCourseLiveClasses] = useState([]);
  const [joinLiveItem, setJoinLiveItem]           = useState(null);

  useEffect(() => {
    if (!courses.length) dispatch(fetchLmsCourses({ school_id: schoolId }));
  }, [dispatch, schoolId, courses.length]);

  // Fetch live classes for this course
  useEffect(() => {
    if (!schoolId) return;
    lmsGetLiveClasses({ school_id: schoolId })
      .then((res) => setCourseLiveClasses(res.data.data || []))
      .catch(() => {});
  }, [schoolId]);

  // Get live class status for a chapter
  const getChapterLiveStatus = (chapterId) => {
    if (!courseLiveClasses.length) return null;
    const now = new Date();
    const match =
      courseLiveClasses.find((lc) => String(lc.chapter_id) === String(chapterId)) ||
      courseLiveClasses.find((lc) => lc.status === "live") ||
      courseLiveClasses.find((lc) => lc.status === "scheduled") ||
      null;
    if (!match) return null;
    if (match.status === "live") return { state: "live", lc: match };
    if (match.status === "ended") return { state: "ended", lc: match };
    const start = new Date(match.scheduled_at);
    const end   = new Date(start.getTime() + (match.duration_minutes || 60) * 60000);
    if (now >= start && now <= end) return { state: "live",      lc: match };
    if (now > end)                  return { state: "ended",     lc: match };
    return                                 { state: "scheduled", lc: match };
  };

  // Load chapters → topics → materials cascade
  const loadCourseContent = async (csId) => {
    if (!csId) return [];

    let rawChapters = [];
    try {
      const chRes = await lmsGetChapters({ class_subject_id: csId });
      rawChapters = Array.isArray(chRes?.data?.data) ? chRes.data.data : [];
    } catch { rawChapters = []; }

    const chaptersWithTopics = await Promise.all(
      rawChapters.filter(Boolean).map(async (ch) => {
        let rawTopics = [];
        try {
          const tpRes = await lmsGetTopics({ chapter_id: ch.id });
          rawTopics = Array.isArray(tpRes?.data?.data) ? tpRes.data.data : [];
        } catch { rawTopics = []; }

        const topicsWithMaterials = await Promise.all(
          rawTopics.filter(Boolean).map(async (tp) => {
            let mats = [];
            try {
              const matRes = await lmsGetMaterials({ topic_id: tp.id });
              mats = Array.isArray(matRes?.data?.data) ? matRes.data.data : [];
            } catch { mats = []; }
            return { ...tp, materials: mats };
          })
        );
        return { ...ch, topics: topicsWithMaterials };
      })
    );
    return chaptersWithTopics;
  };

  useEffect(() => {
    setMatLoading(true);
    setMatError(null);

    const loadData = async () => {
      try {
        let courseData = course;
        if (!courseData?.class_id) {
          try {
            const courseRes = await lmsGetCourseById(id);
            courseData = courseRes?.data?.data ?? courseRes?.data ?? {};
          } catch { courseData = {}; }
        }

        let csId = courseData?.class_subject_id ?? null;
        if (!csId && courseData?.class_id && courseData?.subject_id) {
          try {
            const csRes = await lmsGetClassSubjects({
              class_id: courseData.class_id,
              subject_id: courseData.subject_id,
            });
            const csList = Array.isArray(csRes?.data?.data) ? csRes.data.data : [];
            const match = csList.find(
              (cs) =>
                String(cs.class_id) === String(courseData.class_id) &&
                String(cs.subject_id) === String(courseData.subject_id)
            );
            csId = match?.id ?? (csList[0]?.id ?? null);
          } catch { csId = null; }
        }

        if (!csId) {
          setMatError("No class-subject linked to this course.");
          setMatLoading(false);
          return;
        }
        setClassSubjectId(csId);
        const data = await loadCourseContent(csId);
        setChapters(data || []);
        if (data && data.length > 0) {
          setOpenChapters({ [data[0].id]: true });
          const first = data[0]?.topics?.[0]?.materials?.[0] ?? null;
          if (first) setActiveMaterial(first);
        }
      } catch (err) {
        setMatError(err?.response?.data?.message || "Failed to load course content");
      } finally {
        setMatLoading(false);
      }
    };
    loadData();
  }, [id, course?.class_subject_id]);

  const toggleChapter = (cid) => setOpenChapters((p) => ({ ...p, [cid]: !p[cid] }));

  const totalMaterials = chapters.reduce((s, ch) =>
    s + ch.topics.reduce((s2, t) => s2 + t.materials.length, 0), 0);

  const LEVEL_COLORS = {
    beginner:     { bg: "#dcfce7", color: "#166534" },
    intermediate: { bg: "#fef9c3", color: "#854d0e" },
    advanced:     { bg: "#fee2e2", color: "#991b1b" },
  };
  const lvl = LEVEL_COLORS[course?.level] || LEVEL_COLORS.beginner;

  const TOPBAR_H = 52;
  const PLAYER_H = isMobile ? "35vh" : isTablet ? "50vh" : "60vh";
  const SIDEBAR_W = isMobile ? "100%" : isTablet ? 300 : 380;
  const SIDEBAR_H = isMobile ? "auto" : `calc(100vh - 56px - ${TOPBAR_H}px)`;

  return (
    <div style={{ margin: "-24px" }}>
      {/* ── Course topbar ── */}
      <div style={{
        display: "flex", alignItems: "center", gap: 16, padding: "0 20px",
        height: TOPBAR_H, background: "#1e1b4b", borderBottom: "1px solid #312e81",
        position: "sticky", top: -24, zIndex: 100,
      }}>
        <button onClick={() => navigate(-1)} style={{
          display: "flex", alignItems: "center", gap: 6, background: "transparent",
          border: "none", color: "#a5b4fc", cursor: "pointer", fontWeight: 600,
          fontSize: 13, padding: "4px 8px", borderRadius: 6,
        }}>
          <ArrowLeft size={16} /> Back
        </button>
        <div style={{ width: 1, height: 20, background: "#312e81" }} />
        <div style={{ fontWeight: 700, fontSize: 14, color: "#e0e7ff", flex: 1,
          overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
          {course?.name || "Course Classroom"}
        </div>
        {course?.level && !isMobile && (
          <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px",
            borderRadius: 20, background: lvl.bg, color: lvl.color }}>
            {course.level}
          </span>
        )}
        {!isMobile && (
          <button onClick={() => setSidebarOpen((p) => !p)} title={sidebarOpen ? "Hide content" : "Show content"} style={{
            display: "flex", alignItems: "center", gap: 6, padding: "5px 12px",
            borderRadius: 6, border: "1px solid #312e81", background: sidebarOpen ? "#312e81" : "transparent",
            color: "#a5b4fc", cursor: "pointer", fontSize: 12, fontWeight: 600,
          }}>
            <BookOpen size={14} />
            {sidebarOpen ? "Hide" : "Content"}
          </button>
        )}
      </div>

      {/* ── Main layout ── */}
      <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", alignItems: "flex-start", background: "#fff" }}>
        {/* LEFT — video & description */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ height: PLAYER_H, background: "#0f0f0f" }}>
            <ContentViewer material={activeMaterial} />
          </div>

          {isMobile && <MobileSidebar
            chapters={chapters} matLoading={matLoading} matError={matError}
            totalMaterials={totalMaterials} activeMaterial={activeMaterial}
            setActiveMaterial={setActiveMaterial} openChapters={openChapters}
            toggleChapter={toggleChapter}
          />}

          <div style={{ borderBottom: "2px solid #e5e7eb", padding: "0 24px",
            display: "flex", gap: 0, background: "#fff", position: "sticky",
            top: TOPBAR_H - 24, zIndex: 10 }}>
            <div style={{ padding: "12px 0", marginRight: 24, fontSize: 14, fontWeight: 700,
              color: "#4f46e5", borderBottom: "2px solid #4f46e5", marginBottom: -2 }}>
              Overview
            </div>
          </div>

          <div style={{ padding: "28px 24px", background: "#fff" }}>
            {course ? (
              <>
                <h1 style={{ fontSize: 22, fontWeight: 800, color: "#111827", margin: "0 0 12px" }}>
                  {course.name}
                </h1>
                {course.description && (
                  <p style={{ fontSize: 14, color: "#4b5563", lineHeight: 1.8, marginBottom: 24 }}>
                    {course.description}
                  </p>
                )}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 20, padding: "20px 0",
                  borderTop: "1px solid #e5e7eb", borderBottom: "1px solid #e5e7eb", marginBottom: 20 }}>
                  {course.instructor_name && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#374151" }}>
                      <User size={15} color="#9ca3af" /> <strong>Instructor:</strong>&nbsp;{course.instructor_name}
                    </div>
                  )}
                  {course.language && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#374151" }}>
                      <Globe size={15} color="#9ca3af" /> {course.language}
                    </div>
                  )}
                  {course.duration_days && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#374151" }}>
                      <Clock size={15} color="#9ca3af" /> {course.duration_days} days
                    </div>
                  )}
                  {course.class_name && (
                    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#374151" }}>
                      <BookOpen size={15} color="#9ca3af" /> {course.class_name} — {course.subject_name}
                    </div>
                  )}
                </div>
                {activeMaterial && (
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: "#111827", marginBottom: 4 }}>
                      Now Learning: {activeMaterial.title || activeMaterial.file_name}
                    </div>
                    {activeMaterial.duration_minutes && (
                      <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, color: "#6b7280" }}>
                        <Clock size={12} /> {activeMaterial.duration_minutes} min
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div style={{ textAlign: "center", padding: 40 }}>
                <Loader size={24} className="ms2-spin" />
              </div>
            )}
          </div>
        </div>

        {/* RIGHT — sticky sidebar (read-only for students) */}
        {!isMobile && sidebarOpen && <div style={{
          width: SIDEBAR_W, flexShrink: 0, borderLeft: "1px solid #e5e7eb",
          position: "sticky", top: TOPBAR_H - 24,
          height: SIDEBAR_H, display: "flex", flexDirection: "column",
          background: "#fff", overflow: "hidden",
        }}>
          <div style={{ padding: "14px 16px", borderBottom: "1px solid #e5e7eb",
            background: "#f8fafc", flexShrink: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>Course Content</div>
            {!matLoading && !matError && (
              <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                {chapters.length} chapters · {totalMaterials} lessons
              </div>
            )}
          </div>

          <div style={{ flex: 1, overflowY: "auto", maxHeight: isMobile ? 360 : undefined }}>
            {matLoading ? (
              <div style={{ textAlign: "center", padding: 40 }}>
                <Loader size={22} className="ms2-spin" />
              </div>
            ) : matError ? (
              <div style={{ padding: 24, color: "#6b7280", fontSize: 13, textAlign: "center" }}>{matError}</div>
            ) : chapters.length === 0 ? (
              <div style={{ padding: 24, color: "#9ca3af", fontSize: 13, textAlign: "center" }}>No materials added yet.</div>
            ) : chapters.map((ch, ci) => {
              const chOpen = !!openChapters[ch.id];
              const matCount = ch.topics.reduce((s, t) => s + t.materials.length, 0);
              return (
                <div key={ch.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                  <div style={{ display: "flex", alignItems: "center", background: chOpen ? "#f0f4ff" : "#f9fafb" }}>
                    <button onClick={() => toggleChapter(ch.id)} style={{
                      flex: 1, display: "flex", alignItems: "center", gap: 8,
                      padding: "12px 16px", background: "transparent",
                      border: "none", cursor: "pointer", textAlign: "left",
                    }}>
                      {chOpen ? <ChevronDown size={14} color="#4f46e5" /> : <ChevronRight size={14} color="#9ca3af" />}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, fontSize: 12, color: "#1e1b4b",
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          Ch {ch.chapter_number || ci + 1}. {ch.name}
                        </div>
                        <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 1 }}>
                          {ch.topics.length} topics · {matCount} lessons
                        </div>
                      </div>
                    </button>
                    {(() => {
                      const ls = getChapterLiveStatus(ch.id);
                      if (ls?.state === "live") return (
                        <button onClick={() => setJoinLiveItem(ls.lc)} title="Join live class"
                          style={{
                            flexShrink: 0, display: "flex", alignItems: "center", gap: 4,
                            padding: "3px 8px", borderRadius: 6, border: "none",
                            background: "#16a34a", color: "#fff", cursor: "pointer",
                            fontSize: 11, fontWeight: 700, marginRight: 10,
                            animation: "pulse 1.5s infinite",
                          }}>
                          <Radio size={11} /> Join
                        </button>
                      );
                      if (ls?.state === "scheduled") return (
                        <span title="Live class coming soon" style={{
                          flexShrink: 0, display: "flex", alignItems: "center", gap: 4,
                          padding: "3px 8px", borderRadius: 6,
                          background: "#fef3c7", color: "#d97706",
                          fontSize: 11, fontWeight: 700, marginRight: 10,
                        }}>
                          <Clock size={11} /> Soon
                        </span>
                      );
                      if (ls?.state === "ended") return (
                        <span title="Live class ended" style={{
                          flexShrink: 0, display: "flex", alignItems: "center", gap: 4,
                          padding: "3px 8px", borderRadius: 6,
                          background: "#f3f4f6", color: "#9ca3af",
                          fontSize: 11, fontWeight: 700, marginRight: 10,
                        }}>
                          <StopCircle size={11} /> Ended
                        </span>
                      );
                      return null;
                    })()}
                  </div>

                  {chOpen && ch.topics.map((tp, ti) => (
                    <div key={tp.id}>
                      <div style={{ padding: "7px 12px 5px 36px", fontSize: 11, fontWeight: 700,
                        color: "#7c3aed", background: "#faf5ff",
                        textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {tp.topic_number || ti + 1}. {tp.name}
                        </span>
                      </div>
                      {tp.materials.map((m) => {
                        const isActive = activeMaterial?.id === m.id;
                        return (
                          <button key={m.id} onClick={() => setActiveMaterial(m)} style={{
                            width: "100%", display: "flex", alignItems: "center", gap: 8,
                            padding: "8px 12px 8px 44px", border: "none", textAlign: "left",
                            background: isActive ? "#eef2ff" : "transparent",
                            borderLeft: isActive ? "3px solid #4f46e5" : "3px solid transparent",
                            cursor: "pointer",
                          }}>
                            <MatTypeIcon mat={m} />
                            <span style={{ flex: 1, fontSize: 12,
                              color: isActive ? "#4338ca" : "#374151",
                              fontWeight: isActive ? 600 : 400,
                              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {m.title || m.file_name || "Untitled"}
                            </span>
                            {m.duration_minutes && (
                              <span style={{ fontSize: 11, color: "#9ca3af", flexShrink: 0 }}>{m.duration_minutes}m</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>}
      </div>

      {joinLiveItem && (
        <JoinModal liveClass={joinLiveItem} onClose={() => setJoinLiveItem(null)} studentMode />
      )}
    </div>
  );
}
